import { useEffect, useRef } from 'react';

const useCases = [
  {
    icon: 'ri-building-3-fill',
    title: '샌드위치패널 공장',
    desc: '패널 제조 현장에서 직접 마감캡과 동시 납품·시공. 생산 공정 일체화로 효율을 극대화합니다.',
    image: 'https://readdy.ai/api/search-image?query=sandwich%20panel%20manufacturing%20factory%20interior%20large%20industrial%20building%20metal%20panels%20production%20line%20clean%20professional%20photography&width=600&height=400&seq=uc001&orientation=landscape',
  },
  {
    icon: 'ri-home-gear-fill',
    title: '조립식 건축물',
    desc: '모듈러·조립식 건물의 지붕 패널 접합부 방수 마감에 최적. 현장 설치 속도를 높여줍니다.',
    image: 'https://readdy.ai/api/search-image?query=modular%20prefabricated%20building%20construction%20site%20exterior%20metal%20panel%20assembly%20modern%20architecture%20clean%20sky%20professional%20photograph&width=600&height=400&seq=uc002&orientation=landscape',
  },
  {
    icon: 'ri-box-3-fill',
    title: '컨테이너 건축',
    desc: '컨테이너 개조 건축물의 지붕 누수 방지에 탁월. 좁은 시공 환경에서도 간편하게 설치됩니다.',
    image: 'https://readdy.ai/api/search-image?query=container%20architecture%20modified%20shipping%20container%20building%20rooftop%20metal%20panel%20construction%20modern%20creative%20architecture%20professional%20photograph&width=600&height=400&seq=uc003&orientation=landscape',
  },
  {
    icon: 'ri-hammer-fill',
    title: '지붕 리모델링',
    desc: '기존 건물의 노후 마감캡 교체 공사에 활용. 실리콘 재도포 없이 누수를 즉시 해결합니다.',
    image: 'https://readdy.ai/api/search-image?query=roof%20renovation%20remodeling%20project%20metal%20panel%20replacement%20construction%20workers%20industrial%20building%20professional%20photography%20daylight&width=600&height=400&seq=uc004&orientation=landscape',
  },
  {
    icon: 'ri-plant-fill',
    title: '농축산 시설',
    desc: '축사, 농업 창고, 비닐하우스 구조물 등 습기가 많은 농업 환경에서도 안정적인 방수 성능을 발휘합니다.',
    image: 'https://readdy.ai/api/search-image?query=modern%20livestock%20farm%20agricultural%20facility%20metal%20panel%20roof%20building%20rural%20countryside%20clean%20professional%20photography&width=600&height=400&seq=uc005&orientation=landscape',
  },
  {
    icon: 'ri-ship-fill',
    title: '해외 수출',
    desc: '동남아, 중동 등 고온·다습 환경의 해외 건설 현장에도 대응. 글로벌 시장 진출을 목표로 합니다.',
    image: 'https://readdy.ai/api/search-image?query=international%20export%20shipping%20port%20container%20cargo%20global%20trade%20commerce%20professional%20photography%20modern%20industrial&width=600&height=400&seq=uc006&orientation=landscape',
  },
];

const UseCasesSection = () => {
  const titleRef = useRef<HTMLDivElement>(null);
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('opacity-100', 'translate-y-0');
        });
      },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    refs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="usecases" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* 타이틀 */}
        <div
          ref={titleRef}
          className="text-center mb-16 opacity-0 translate-y-6 transition-all duration-700"
        >
          <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">Application</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1B2A4A] font-display">
            이런 곳에 사용됩니다
          </h2>
          <p className="mt-4 text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
            샌드위치패널이 사용되는 모든 건축 현장에 적용 가능합니다
          </p>
          <div className="mt-4 w-12 h-1 bg-[#FF6B35] mx-auto rounded-full" />
        </div>

        {/* 카드 그리드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {useCases.map((item, i) => (
            <div
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              className="group rounded-xl overflow-hidden border border-gray-100 opacity-0 translate-y-6 transition-all duration-500 hover:border-[#FF6B35]/40 hover:-translate-y-1 cursor-pointer"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* 이미지 */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A4A]/75 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FF6B35] group-hover:scale-110 transition-transform duration-300">
                    <i className={`${item.icon} text-white text-sm`} />
                  </div>
                  <span className="text-white font-bold text-sm drop-shadow">{item.title}</span>
                </div>
              </div>
              {/* 설명 */}
              <div className="p-5">
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
